// =============================================================================
// Team Permissions Hook
// =============================================================================
// Provides permission-checking for TEAM role users.
// ADMIN users always have full access. TEAM users are checked against their
// TeamMember.permissions JSON fetched from the backend.
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { adminService } from '../services/admin.service';
import type { TeamPermissions, MyPermissionsResponse } from '../services/admin.service';

// =============================================================================
// Types
// =============================================================================

type PermissionArea =
  | 'dashboard'
  | 'investors'
  | 'deals'
  | 'distributions'
  | 'documents'
  | 'communications'
  | 'fundraising'
  | 'accounting'
  | 'auditLog';

type PermissionAction = 'view' | 'create' | 'edit' | 'delete' | 'upload' | 'manage' | 'sensitive';

interface UsePermissionsReturn {
  /** Check if user has a specific permission */
  hasPermission: (area: PermissionArea, action: PermissionAction) => boolean;
  /** Whether the user is a full admin (not TEAM) */
  isAdmin: boolean;
  /** Whether the user is a TEAM role user */
  isTeam: boolean;
  /** Whether permissions have been loaded */
  isLoaded: boolean;
  /** The raw permissions object (null for ADMIN users) */
  permissions: TeamPermissions | null;
  /** The list of assigned deal IDs (empty for ADMIN or all-deals TEAM users) */
  assignedDealIds: string[];
}

// =============================================================================
// Singleton cache to avoid redundant API calls across components
// =============================================================================

let cachedPermissions: MyPermissionsResponse | null = null;
let fetchPromise: Promise<MyPermissionsResponse> | null = null;

// =============================================================================
// Hook
// =============================================================================

export function usePermissions(): UsePermissionsReturn {
  const { user, isAuthenticated } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const isTeam = user?.role === 'TEAM';

  const [permData, setPermData] = useState<MyPermissionsResponse | null>(cachedPermissions);
  const [isLoaded, setIsLoaded] = useState(isAdmin || !!cachedPermissions);

  useEffect(() => {
    if (!isAuthenticated || !isTeam) {
      setIsLoaded(true);
      return;
    }

    // Use cached data if available
    if (cachedPermissions) {
      setPermData(cachedPermissions);
      setIsLoaded(true);
      return;
    }

    // Deduplicate concurrent fetches
    if (!fetchPromise) {
      fetchPromise = adminService.getMyPermissions();
    }

    fetchPromise
      .then((data) => {
        cachedPermissions = data;
        setPermData(data);
        setIsLoaded(true);
      })
      .catch(() => {
        setIsLoaded(true);
      })
      .finally(() => {
        fetchPromise = null;
      });
  }, [isAuthenticated, isTeam]);

  const hasPermission = useCallback(
    (area: PermissionArea, action: PermissionAction): boolean => {
      // ADMIN always has full access
      if (isAdmin) return true;

      // Not a TEAM user or permissions not loaded
      if (!isTeam || !permData?.permissions) return false;

      const areaPerms = permData.permissions.areas[area];
      if (!areaPerms) return false;

      return (areaPerms as Record<string, boolean>)[action] === true;
    },
    [isAdmin, isTeam, permData]
  );

  const assignedDealIds = permData?.permissions?.assignedDealIds || [];

  return {
    hasPermission,
    isAdmin,
    isTeam,
    isLoaded,
    permissions: permData?.permissions || null,
    assignedDealIds,
  };
}

/** Clear the cached permissions (call on logout) */
export function clearPermissionsCache() {
  cachedPermissions = null;
  fetchPromise = null;
}

export default usePermissions;
